import { type FC, useEffect, useState } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAccountDeletionRequests } from '../hooks/useAccountDeletionRequests';
import type { AccountDeletionRequest, AccountDeletionStatus } from '../services/accountDeletionService';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDateTime = (value: string | null) =>
    value ? new Date(value).toLocaleString() : '—';

const STATUS_BADGE: Record<AccountDeletionStatus, { label: string; className: string }> = {
    pending: {
        label: 'Pending Verification',
        className: 'bg-stone-100 text-stone-600 ring-stone-200',
    },
    confirmed: {
        label: 'Awaiting Review',
        className: 'bg-blue-50 text-blue-700 ring-blue-200',
    },
    actioned: {
        label: 'Actioned',
        className: 'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]',
    },
    expired: {
        label: 'Expired',
        className: 'bg-amber-50 text-amber-700 ring-amber-200',
    },
    cancelled: {
        label: 'Cancelled',
        className: 'bg-rose-50 text-rose-700 ring-rose-200',
    },
};

const StatusBadge: FC<{ status: AccountDeletionStatus }> = ({ status }) => {
    const badge = STATUS_BADGE[status];
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${badge.className}`}>
            {badge.label}
        </span>
    );
};

// ── Confirm-action modal ─────────────────────────────────────────────────────
// Actioning a request is irreversible (blocks login), so it gets an explicit
// confirmation step rather than acting straight off the table row button.

const ConfirmActionModal: FC<{
    request: AccountDeletionRequest;
    busy: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}> = ({ request, busy, onConfirm, onCancel }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 px-4">
        <div className="w-full max-w-md rounded-2xl bg-white shadow-xl overflow-hidden">
            <div className="h-1 bg-[#ee2b74]" />
            <div className="p-6">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-200">
                        <ExclamationTriangleIcon className="h-5 w-5" />
                    </div>
                    <div>
                        <h3 className="text-base font-semibold text-stone-900">Action this deletion request?</h3>
                        <p className="mt-1 text-sm text-stone-600 leading-6">
                            This will permanently block login for <span className="font-medium text-stone-800">{request.email}</span>.
                            Order and payment history is retained for financial records. This cannot be undone.
                        </p>
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="inline-flex items-center justify-center rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={busy}
                        aria-busy={busy}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#ee2b74] px-4 py-2.5 text-sm font-semibold text-white shadow-sm shadow-pink-200 hover:bg-[#d4245f] transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {busy ? 'Blocking access…' : 'Confirm & block account'}
                    </button>
                </div>
            </div>
        </div>
    </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

const AccountDeletionRequestsPage: FC = () => {
    const { requests, loading, error, actioningId, fetchRequests, actionRequest } = useAccountDeletionRequests();
    const [confirmTarget, setConfirmTarget] = useState<AccountDeletionRequest | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    useEffect(() => {
        void fetchRequests();
    }, [fetchRequests]);

    const pendingReviewCount = requests.filter(r => r.status === 'confirmed').length;

    const handleConfirm = async () => {
        if (!confirmTarget) return;
        setActionError(null);
        const result = await actionRequest(confirmTarget.id);
        if (result.success) {
            setConfirmTarget(null);
        } else {
            setActionError(result.message);
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-semibold text-stone-900">Account Deletion Requests</h2>
                <p className="mt-1 text-sm text-stone-500">
                    Review verified account deletion requests. Actioning a request blocks the customer's
                    login while retaining order and payment history for financial records.
                </p>
            </div>

            {pendingReviewCount > 0 && (
                <div className="rounded-2xl border border-blue-200 bg-blue-50 px-5 py-4 flex items-center gap-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-blue-700 flex-shrink-0" />
                    <p className="text-sm text-blue-800">
                        <span className="font-semibold">{pendingReviewCount}</span> request{pendingReviewCount === 1 ? '' : 's'} awaiting review.
                    </p>
                </div>
            )}

            {error && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
                    {error}
                </div>
            )}

            <div className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
                <div className="h-1 bg-[#ee2b74]" />
                <div className="px-6 py-4 border-b border-stone-200 bg-stone-50/60">
                    <h4 className="text-sm font-semibold text-stone-900">All Requests</h4>
                    <p className="text-xs text-stone-500 mt-1">Every deletion request, regardless of status.</p>
                </div>
                <div className="overflow-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-white border-b border-stone-200">
                        <tr>
                            {['Email', 'Requested', 'Confirmed', 'Status', 'Actioned', ''].map(h => (
                                <th key={h} className="py-3 px-6 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                                    {h}
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                        {loading ? (
                            <tr><td colSpan={6} className="py-10 px-6 text-stone-400">Loading…</td></tr>
                        ) : requests.length === 0 ? (
                            <tr><td colSpan={6} className="py-10 px-6 text-stone-400">No deletion requests found</td></tr>
                        ) : requests.map((request) => (
                            <tr key={request.id} className="hover:bg-stone-50/50 transition-colors">
                                <td className="py-4 px-6 font-medium text-stone-900">{request.email}</td>
                                <td className="py-4 px-6 text-stone-600">{formatDateTime(request.created_at)}</td>
                                <td className="py-4 px-6 text-stone-600">{formatDateTime(request.confirmed_at)}</td>
                                <td className="py-4 px-6">
                                    <StatusBadge status={request.status} />
                                </td>
                                <td className="py-4 px-6 text-stone-600">{formatDateTime(request.actioned_at)}</td>
                                <td className="py-4 px-6 text-right">
                                    {request.status === 'confirmed' && (
                                        <button
                                            type="button"
                                            onClick={() => { setActionError(null); setConfirmTarget(request); }}
                                            disabled={actioningId === request.id}
                                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-2 text-xs font-semibold text-rose-600 shadow-sm hover:bg-rose-50 hover:border-rose-200 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            <TrashIcon className="h-3.5 w-3.5" />
                                            Action
                                        </button>
                                    )}
                                    {request.status === 'actioned' && (
                                        <span className="inline-flex items-center gap-1 text-xs text-stone-400">
                                                <CheckCircleIcon className="h-4 w-4" />
                                                Complete
                                            </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {confirmTarget && (
                <ConfirmActionModal
                    request={confirmTarget}
                    busy={actioningId === confirmTarget.id}
                    onConfirm={handleConfirm}
                    onCancel={() => { setConfirmTarget(null); setActionError(null); }}
                />
            )}

            {actionError && !confirmTarget && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
                    {actionError}
                </div>
            )}
        </div>
    );
};

export default AccountDeletionRequestsPage;